/* eslint-disable import/order */

import React, { FC, useEffect, useState } from 'react';
import { Modal, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import { Alert, Button, DocumentInput, Form, Space } from '~/components';
import { useUploadSignedContract, useGenerateContract } from '~/controllers/contract';
import { WarningCircleIcon, ContractGenerationIcon } from '~/img';
import WizardContent from '~/layouts/WizardContent';
import Section from '~/layouts/Section ';
import { ImageResponse } from '~/types/files';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import paths from '~/pages/paths';
import { useGetApplication } from '~/controllers/wizard';
import LeftLayout from '~/layouts/LeftLayout';
import BottomLeftActions from '~/layouts/BottomLeftActions';
import features from '~/constants/features';
import { isReadOnly } from '~/utils/isReadOnly';
import { getUaePassEmail, isLoggedInWithUaePass } from '~/utils/getDecodedJwt';
import UaePassButton from '~/components/UaePassButton';
import { fetchDocumentFromUaePass, signWithUaePass } from '~/api/contract';
import { useAuthorizedSignatories } from '~/hooks';
import { UAEPASS_DISABLE } from '~/env';
import ApplicationCommonErrors from '../ApplicationCommonErrors';
import DocumentsSharingProgressModal from './DocumentsSharingProgressModal';
import SignatorySection from './SignatorySection';
import SaveSignedContract from './SaveSignedContract';
import SendInvitationModal from './SendInvitationModal';
import SaveContract from './SaveContract';

const UaePassSignContract: FC = () => {
  const { t } = useTranslation('contract');
  const applicationQuery = useGetApplication();
  const generateContractMutation: any = useGenerateContract();
  const uploadContractMutation = useUploadSignedContract();
  const location = useLocation();
  const history = useHistory();

  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const readOnly = isReadOnly(applicationQuery.data);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    void applicationQuery.refetch();
  };

  const {
    isMultipleSignatories,
    selectedAuthorizedRepresentativeIndex,
    authorizedSignatoryEmail,
    setAuthorizedSignatoryEmail,
    resultingAuthorizedRepresentative,
  } = useAuthorizedSignatories();

  const currentUserEmail = getUaePassEmail();
  const isCurrentUserAuthorizedSignatory = authorizedSignatoryEmail === currentUserEmail;

  const handleGenerateClick = () => {
    if (isMultipleSignatories === null) return;
    if (isMultipleSignatories && !resultingAuthorizedRepresentative) {
      handleOpen();
    } else {
      generateContractMutation.mutate();
    }
  };

  const selectedSignatoryUaePassSub =
    (selectedAuthorizedRepresentativeIndex >= 0 &&
      applicationQuery.data?.payload.uaepassDocumentSharing &&
      applicationQuery.data?.payload.uaepassDocumentSharing[selectedAuthorizedRepresentativeIndex === 0 ? '0' : '1']) ||
    undefined;

  const onFinish = async (values: { data: ImageResponse | ImageResponse[] }) => {
    try {
      if (Array.isArray(values.data)) {
        await uploadContractMutation.mutateAsync(values.data);
      } else {
        await uploadContractMutation.mutateAsync([values.data]);
      }
      history.push(paths.home._);
    } catch (error) {
      console.error('An error occurred while proceeding to the next step', error);
      setIsLoading(false);
    }
  };

  const signatorySection = isMultipleSignatories && (
    <>
      <SignatorySection
        resultingAuthorizedRepresentative={
          resultingAuthorizedRepresentative && typeof resultingAuthorizedRepresentative !== 'string'
            ? resultingAuthorizedRepresentative
            : undefined
        }
        onOpen={handleOpen}
        readOnly={readOnly}
        authorizedSignatoryEmail={authorizedSignatoryEmail}
        isCurrentUserAuthorizedSignatory={isCurrentUserAuthorizedSignatory}
      />
    </>
  );

  const uaePassSignatureProcessId = applicationQuery.data?.payload.uaePassSignatureProcess?.id;
  const isSignatureProcessInProgress = !!uaePassSignatureProcessId;

  const [uaePassDocument, setUaePassDocument] = useState();
  const isUaePass = isLoggedInWithUaePass();
  const [isSendingToUaePass, setSendingToUaePass] = useState(false);
  const [isUaePassError, setUaePassError] = useState(false);

  const isUaePassAllowed = isUaePass && isCurrentUserAuthorizedSignatory;
  const isSigningForbidden = !isUaePass && selectedSignatoryUaePassSub !== undefined;

  useEffect(() => {
    if (isUaePassError) {
      Modal.error({
        content: (
          <div style={{ paddingLeft: 40 }}>
            {t(
              "Failed to fetch information from UAE PASS. Press on 'Close' button below to reload this page and try again.",
            )}
          </div>
        ),
        onOk: () => {
          window.location.reload();
        },
        centered: true,
        okText: t('Close'),
      });
    }
  }, [isUaePassError, t]);

  useEffect(() => {
    if (uaePassSignatureProcessId && setUaePassDocument) {
      fetchDocumentFromUaePass()
        .then((response) => {
          console.log(response);
          setUaePassDocument(response);
        })
        .catch(() => {
          setUaePassError(true);
        });
    }
  }, [uaePassSignatureProcessId]);

  const handleSignWithUaePassClick = () => {
    setSendingToUaePass(true);
    signWithUaePass()
      .then((response: any) => {
        console.log(response.redirectUrl);
        window.location.href = response.redirectUrl;
      })
      .catch(() => {
        setUaePassError(true);
      });
  };

  const showContinue = uaePassDocument || (!isSignatureProcessInProgress && !isUaePassAllowed && !isSigningForbidden);

  return (
    <>
      <SendInvitationModal
        open={open}
        onClose={handleClose}
        onInvitationSent={generateContractMutation.mutate}
        authorizedSignatoryEmail={authorizedSignatoryEmail}
        setAuthorizedSignatoryEmail={setAuthorizedSignatoryEmail}
      />
      <WizardContent
        content={
          <Container>
            <Typography.Title>{t('Commercial Card Application')}</Typography.Title>
            {uaePassDocument ? (
              <Section formItemLast>
                <Typography.Paragraph>{t('Great! You have successfully signed the contract.')}</Typography.Paragraph>
                <>
                  <Form
                    name={location.pathname}
                    onFinish={onFinish}
                    initialValues={{
                      data: uaePassDocument,
                    }}
                  >
                    <Form.Item name="data" rules={[{ required: true }]} hidden>
                      <DocumentInput name="photos" variant="default" disabled={readOnly} />
                    </Form.Item>
                  </Form>
                  <SaveSignedContract value={uaePassDocument} />
                </>
              </Section>
            ) : isSignatureProcessInProgress ? (
              <>
                {!UAEPASS_DISABLE && (
                  <DocumentsSharingProgressModal
                    visible={!isUaePassError}
                    message="Processing your contract signing with UAE PASS. The signed contract will load once signing process is complete."
                  />
                )}
                <Skeleton />
              </>
            ) : !generateContractMutation.data ? (
              <Section formItemLast>
                {signatorySection}
                <Typography.Paragraph>
                  {t('To generate the commercial card application please press the button below.')}
                </Typography.Paragraph>
                <Space direction="vertical" size={24}>
                  <Button
                    id="signContract_generateContract"
                    extraLarge
                    icon={<ContractGenerationIcon />}
                    type="default"
                    onClick={handleGenerateClick}
                    loading={generateContractMutation.isLoading || applicationQuery.isLoading}
                    disabled={applicationQuery.isLoading || readOnly}
                  >
                    {t('Generate Commercial Card Application')}
                  </Button>
                </Space>
              </Section>
            ) : (
              <>
                {signatorySection}
                <Section formItemLast>
                  <Typography.Title level={5}>{t('Download contract')}</Typography.Title>
                  <SaveContract name={generateContractMutation.data.name} data={generateContractMutation.data.blob} />
                </Section>
                <Section>
                  {isUaePassAllowed ? (
                    <>
                      <Typography.Title level={5}>{t('Sign and submit')}</Typography.Title>
                      <Space direction="vertical" size={24}>
                        <UaePassButton
                          id="signContract_signWithUaePass"
                          onClick={handleSignWithUaePassClick}
                          title={t('Sign with UAE PASS')}
                        />
                      </Space>
                      {!UAEPASS_DISABLE && isSendingToUaePass && (
                        <DocumentsSharingProgressModal
                          visible
                          message="Processing your contract signing with UAE PASS. The signed contract will load once signing process is complete."
                        />
                      )}
                    </>
                  ) : (
                    isSigningForbidden && (
                      <>
                        <Typography.Title level={5}>{t('Sign and submit')}</Typography.Title>
                        <Alert
                          message={t('Not allowed to sign')}
                          description={t(
                            'Contract should be signed with UAE PASS! Login via UAE PASS to sign document.',
                          )}
                          type="warning"
                          icon={<WarningCircleIcon />}
                          showIcon
                        />
                      </>
                    )
                  )}
                </Section>
              </>
            )}
          </Container>
        }
        left={
          <LeftLayout>
            <BottomLeftActions contactUsUrl={features.contactUsLinkContract} />
          </LeftLayout>
        }
        right={
          <Space direction="vertical" size={24}>
            <ApplicationCommonErrors />
            {showContinue && (
              <WizardContinueButton
                id="contract_continueButton"
                formName={location.pathname}
                disabled={
                  !uaePassDocument || (selectedAuthorizedRepresentativeIndex >= 0 && !isCurrentUserAuthorizedSignatory)
                }
                loading={uploadContractMutation.isLoading || isLoading}
              />
            )}
          </Space>
        }
      />
    </>
  );
};

const Container = styled.div`
  h1 {
    margin-bottom: 16px;
  }
  .SignContract-Subtitle {
    margin-bottom: 24px;
  }
`;

export default UaePassSignContract;
